
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, Snackbar, Alert, CircularProgress,
    Avatar, Grow, Tooltip, TextField, InputAdornment
} from '@mui/material';
import {
    History, Search, FilterList, Refresh, Security, Terminal, Save, Delete,
    ManageAccounts, CloudUpload
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../App';

function AuditLogsPage() {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    const fetchLogs = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get('/api/v1/audit/', { headers });
            setLogs(response.data);
        } catch (err) {
            setError('Failed to retrieve system audit trail.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [token]);

    const getActionIcon = (action) => {
        if (action.includes('DEVICE')) return <Terminal sx={{ fontSize: 18 }} />;
        if (action.includes('FIRMWARE')) return <CloudUpload sx={{ fontSize: 18 }} />;
        if (action.includes('ALLOCATED')) return <ManageAccounts sx={{ fontSize: 18 }} />;
        return <Save sx={{ fontSize: 18 }} />;
    };

    const getActionColor = (action) => {
        if (action.includes('DELETED')) return 'error';
        if (action.includes('CREATED') || action.includes('UPLOADED')) return 'success';
        if (action.includes('UPDATED')) return 'warning';
        return 'primary';
    };

    const filteredLogs = logs.filter(log =>
        log.action.toLowerCase().includes(search.toLowerCase()) ||
        log.resource_type.toLowerCase().includes(search.toLowerCase()) ||
        (log.resource_id && log.resource_id.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        Immutable Audit Trail
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        System-wide traceability for all administrative operations
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search matrix..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 3, bgcolor: 'rgba(11,18,36,0.9)', width: 250 }
                        }}
                    />
                    <IconButton onClick={fetchLogs} sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)', color: 'primary.main' }}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            <Grow in>
                <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 4, overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>Timestamp</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Operator Context</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Operation Action</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Resource Matrix</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Execution Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log) => (
                                <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 180, 219, 0.02)' } }}>
                                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        {new Date(log.created_at).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.light' }}>
                                                U
                                            </Avatar>
                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                                {log.user_id ? `Operator #${log.user_id}` : 'SYSTEM'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            color={getActionColor(log.action)}
                                            icon={getActionIcon(log.action)}
                                            sx={{ fontWeight: 800, borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ fontWeight: 700, p: 0.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                                            {log.resource_type.toUpperCase()}: {log.resource_id || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={JSON.stringify(log.details, null, 2)}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>
                                                {log.ip_address} | {log.details ? 'View Parameters' : 'No Details'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredLogs.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                        <Security sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            No audit records found in the current matrix.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grow>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 700 }}>{error}</Alert>
            </Snackbar>
        </Box>
    );
}

export default AuditLogsPage;
